import { useQuery } from '@tanstack/react-query';
import { Star, TrendingUp } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { partnerApi } from '../../../lib/api';

interface ReputationMetricsData {
  tenantsWithReputationEnabled: number;
  tenantDetails: Array<{
    businessId: string;
    businessName: string;
    averageGoogleRating: number | null;
    totalGoogleReviews: number;
  }>;
}

export const ReputationMetrics = () => {
  const { data: metrics, isLoading } = useQuery<ReputationMetricsData>({
    queryKey: ['reputation-metrics'],
    queryFn: () => partnerApi.getReputationMetrics(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading metrics...</div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">
              Tenants with Reputation Enabled
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {metrics.tenantsWithReputationEnabled}
            </p>
          </div>
          <div className="h-12 w-12 rounded-full bg-yellow-50 flex items-center justify-center">
            <Star className="h-6 w-6 text-yellow-600" />
          </div>
        </div>
      </Card>

      {/* Tenant Details Table */}
      {metrics.tenantDetails.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              <TrendingUp className="inline h-5 w-5 text-yellow-600 mr-2" />
              Tenants with Reputation Management
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Average Google Rating
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Google Reviews
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.tenantDetails.map(tenant => (
                  <tr key={tenant.businessId} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tenant.businessName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {tenant.averageGoogleRating !== null ? (
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-500 mr-1 fill-yellow-500" />
                          <span className="text-gray-900 font-medium">
                            {tenant.averageGoogleRating.toFixed(1)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No ratings</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tenant.totalGoogleReviews}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
